import type { TokenizerAndRendererExtension, Tokens } from 'marked';

/**
 * G-2 — Marked block extension that recognises GitHub-style admonitions
 * inside the moderator guide markdown:
 *
 *   > [!TIP]
 *   > Body text. Multiple lines collapse with the rest of the blockquote.
 *
 *   > [!WARNING]
 *   > Body text.
 *
 *   > [!EXAMPLE]
 *   > Body text.
 *
 * Emits HTML that matches the React `<Callout>` primitive's DOM tree
 * (aside.callout.callout--<variant> with header + body). Because the
 * GuidePage renders the markdown via `dangerouslySetInnerHTML` we don't
 * need to splice in a React subtree — the same CSS styles both paths.
 *
 * Accessibility roles mirror the React component:
 *   - tip      → role="note"
 *   - warning  → role="alert"
 *   - example  → role="region" with aria-label
 */

type Variant = 'tip' | 'warning' | 'example';

const VARIANT_LABEL: Record<Variant, string> = {
  tip: 'Tip',
  warning: 'Warning',
  example: 'Example',
};

const VARIANT_ROLE: Record<Variant, string> = {
  tip: 'note',
  warning: 'alert',
  example: 'region',
};

const VARIANT_ICON: Record<Variant, string> = {
  tip: '<svg class="callout__icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M8 1.5a4.5 4.5 0 0 0-2.5 8.25V12a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1V9.75A4.5 4.5 0 0 0 8 1.5z" stroke="currentColor" stroke-width="1.4" fill="none" stroke-linejoin="round"/><line x1="6.5" y1="14.5" x2="9.5" y2="14.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>',
  warning: '<svg class="callout__icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M8 1.5 14.5 13H1.5L8 1.5z" stroke="currentColor" stroke-width="1.4" fill="none" stroke-linejoin="round"/><line x1="8" y1="6" x2="8" y2="9.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><circle cx="8" cy="11.25" r="0.75" fill="currentColor"/></svg>',
  example: '<svg class="callout__icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><rect x="2" y="3" width="12" height="10" rx="1.5" stroke="currentColor" stroke-width="1.4" fill="none"/><line x1="4.5" y1="6" x2="11.5" y2="6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><line x1="4.5" y1="8.5" x2="9" y2="8.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><line x1="4.5" y1="11" x2="8" y2="11" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>',
};

interface CalloutToken extends Tokens.Generic {
  type: 'guideCallout';
  raw: string;
  variant: Variant;
  body: string;
  tokens: Tokens.Generic[];
}

/**
 * Match a contiguous block-quote that starts with `> [!TIP]` (or WARNING,
 * EXAMPLE) on the first line. We consume every line of the blockquote
 * (`>`-prefixed, with the optional space) and stop when we hit a line that
 * isn't a blockquote line, the end of `src`, or two consecutive blank
 * lines — same convention as standard markdown blockquotes.
 */
function tokenizeCallout(src: string): CalloutToken | undefined {
  // Must start at line start (block-level)
  const firstLineMatch = /^>\s*\[!(TIP|WARNING|EXAMPLE)\]\s*\n/i.exec(src);
  if (!firstLineMatch) return undefined;

  const variant = firstLineMatch[1].toLowerCase() as Variant;
  const lines = src.split('\n');
  // Discard the first marker line; collect the rest of the blockquote.
  const bodyLines: string[] = [];
  let consumed = lines[0].length + 1; // first line + \n
  for (let i = 1; i < lines.length; i += 1) {
    const line = lines[i];
    if (/^>\s?/.test(line)) {
      bodyLines.push(line.replace(/^>\s?/, ''));
      consumed += line.length + 1;
    } else if (line.trim() === '' && bodyLines.length > 0) {
      // A single blank line ends the blockquote.
      consumed += line.length + 1;
      break;
    } else {
      break;
    }
  }
  const body = bodyLines.join('\n');
  const raw = src.slice(0, consumed);
  return {
    type: 'guideCallout',
    raw,
    variant,
    body,
    tokens: [],
  };
}

export const calloutExtension: TokenizerAndRendererExtension = {
  name: 'guideCallout',
  level: 'block',
  // `start` lets marked skip ahead efficiently — return the offset of the
  // next potential match so the tokenizer isn't tried on every char.
  start(src: string) {
    const m = /^>\s*\[!(TIP|WARNING|EXAMPLE)\]/m.exec(src);
    return m ? m.index : undefined;
  },
  tokenizer(src: string) {
    const token = tokenizeCallout(src);
    if (!token) return undefined;
    // Recursively lex the body so nested markdown (links, lists, code)
    // still renders. `this.lexer.blockTokens` mutates an array, hence the
    // empty-array start.
    this.lexer.blockTokens(token.body, token.tokens as Tokens.Generic[]);
    return token;
  },
  renderer(token) {
    const t = token as CalloutToken;
    const variant = t.variant;
    const label = VARIANT_LABEL[variant];
    const role = VARIANT_ROLE[variant];
    const icon = VARIANT_ICON[variant];
    const labelId = `callout-${variant}-${Math.random().toString(36).slice(2, 7)}`;
    const ariaLabel =
      role === 'region' ? ` aria-labelledby="${labelId}"` : '';
    const inner = this.parser.parse(t.tokens || []);
    return [
      `<aside class="callout callout--${variant}" role="${role}"${ariaLabel} data-variant="${variant}" data-testid="callout-${variant}">`,
      `<div class="callout__header">`,
      icon,
      `<span class="callout__title" id="${labelId}">${label}</span>`,
      `</div>`,
      `<div class="callout__body">${inner}</div>`,
      `</aside>`,
    ].join('');
  },
};
